import { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider';
import LocalAuthProvider from './contexts/LocalAuthProvider';
import OdooAuthProvider from './contexts/OdooAuthProvider';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Orders from './pages/Orders';
import Receive from './pages/Receive';
import Admin from './pages/Admin';
import Receipts from './pages/Receipts';
import Photos from './pages/Photos';
import { applyTheme, loadTheme } from './lib/store';

const AUTH_BYPASS = import.meta.env.VITE_AUTH_BYPASS === 'true';
const AUTH_MODE = AUTH_BYPASS ? 'bypass' : import.meta.env.VITE_AUTH_MODE || 'odoo';

/* A new screen starts at the top. On a phone the page stays taller than the
   visible screen while the browser bar shows, so the old scroll survives even
   on a short page and the first line starts under the sticky header. Going back
   to the list is the one place the browser may keep its position. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const type = useNavigationType();
  useLayoutEffect(() => {
    if (type !== 'POP' || pathname !== '/') window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    applyTheme(loadTheme());
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme(loadTheme());
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);

  const Provider = AUTH_MODE === 'bypass' ? LocalAuthProvider : AUTH_MODE === 'odoo' ? OdooAuthProvider : AuthProvider;
  const clerk = AUTH_MODE === 'clerk';
  return (
    <Provider>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={clerk ? <Login /> : <Navigate to="/" replace />} />
          <Route path="/sso-callback" element={clerk ? <SSOCallback /> : <Navigate to="/" replace />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Orders />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <PrivateRoute>
                <Receive />
              </PrivateRoute>
            }
          />
          <Route
            path="/receive/:ids"
            element={
              <PrivateRoute>
                <Receive />
              </PrivateRoute>
            }
          />
          <Route
            path="/photos"
            element={
              <PrivateRoute>
                <Photos />
              </PrivateRoute>
            }
          />
          <Route
            path="/receipts"
            element={
              <PrivateRoute>
                <Receipts />
              </PrivateRoute>
            }
          />
          <Route
            path="/usage"
            element={
              <PrivateRoute>
                <Admin />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </Provider>
  );
}
